import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useLocalCamera — Instant camera preview via getUserMedia.
 *
 * Calls getUserMedia directly on mount for immediate camera feed.
 * Returns a video ref that can be attached to a <video> element.
 *
 * @param {boolean} enabled — whether the camera should be active
 * @param {'user'|'environment'} facingMode — front or back camera
 * @returns {{ videoRef, isReady, error, stop }}
 */
export function useLocalCamera(enabled = true, facingMode = 'environment') {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsReady(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: teardown when disabled
      stop();
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        // Use pre-warmed camera stream if available (started in main.jsx during splash)
        let stream = null;
        if (window.__earlyCameraPromise) {
          const promise = window.__earlyCameraPromise;
          window.__earlyCameraPromise = null; // consume once
          stream = await promise;
        }

        // Fall back to fresh getUserMedia if pre-warm unavailable or failed
        if (!stream) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: false,
          });
        }

        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        setIsReady(true);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          console.error('[useLocalCamera] getUserMedia failed:', err);
          setError(err);
          setIsReady(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      // Don't stop stream on cleanup — let the caller manage lifecycle
      // via the `enabled` prop or explicit `stop()` call
    };
  }, [enabled, facingMode]);

  // Stop stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Note: stream is accessed via videoRef.current.srcObject by callers,
  // not returned directly (accessing refs during render is not allowed)
  return { videoRef, isReady, error, stop };
}
